import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Building2, Loader2 } from 'lucide-react';
import { api, MAX_ORGANIZATIONS, type Organization } from '../lib/api';
import { Drawer } from './ui/Drawer';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Textarea } from './ui/Textarea';

interface Props {
  /** Already-loaded organizations, used for the local name and headroom checks. */
  existing: Organization[];
  onClose: () => void;
}

export function CreateOrganizationModal({ existing, onClose }: Props) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [description, setDescription] = useState('');

  // Advisory only. The server re-checks both, and it is the authority — names
  // are not a key, so nothing here can be a guarantee.
  const duplicate = useMemo(() => {
    const needle = name.trim().toLowerCase();
    if (!needle) return null;
    return existing.find((o) => (o.name ?? '').toLowerCase() === needle) ?? null;
  }, [existing, name]);
  const atCapacity = existing.length >= MAX_ORGANIZATIONS;

  const create = useMutation({
    mutationFn: () =>
      api.createOrganization({
        name: name.trim(),
        ...(company.trim() ? { company: company.trim() } : {}),
        ...(description.trim() ? { description: description.trim() } : {}),
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
      onClose();
      // Land on the new organization — the next thing to do is almost always
      // attach its users and applications.
      navigate(`/organizations/${result.item.id}`);
    },
  });

  const valid = name.trim().length > 0 && !atCapacity;

  return (
    <Drawer
      open
      onClose={onClose}
      className="max-w-lg"
      title={
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            New organization
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {existing.length} of {MAX_ORGANIZATIONS} used
          </p>
        </div>
      }
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate()} disabled={!valid || create.isPending}>
            {create.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Creating…
              </>
            ) : (
              'Create organization'
            )}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) create.mutate();
        }}
      >
        {atCapacity && (
          <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium">Limit reached</p>
              <p className="mt-1 text-xs">
                All {MAX_ORGANIZATIONS} organization slots are in use. Retire one before adding
                another.
              </p>
            </div>
          </div>
        )}

        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Name</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            required
            autoFocus
            placeholder="Acme Laboratories"
          />
          {duplicate && (
            <p className="mt-1 text-xs text-warning">
              An organization called “{duplicate.name}” already exists. The server will refuse an
              exact match.
            </p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Company (optional)
          </label>
          <Input
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            maxLength={200}
            placeholder="Legal or parent company name"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Description (optional)
          </label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            placeholder="What this organization covers"
          />
        </div>

        {create.isError && (
          <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium">Create failed</p>
              <p className="mt-1 text-xs">{(create.error as Error).message}</p>
            </div>
          </div>
        )}

        <div className="rounded-md border border-line bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          <p className="mb-1 font-medium text-foreground">What happens on submit</p>
          <ul className="list-disc space-y-0.5 pl-4">
            <li>
              A row is written to the Amplify <code className="font-mono">Organization</code> table
              with a new UUID.
            </li>
            <li>
              There is no delete through this portal — an organization can be renamed and emptied,
              but not removed.
            </li>
            <li>Users and applications are attached afterwards, from the organization's page.</li>
          </ul>
        </div>
      </form>
    </Drawer>
  );
}
